import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCcw, Award, CheckCircle } from 'lucide-react';
import { useAuth } from '../AuthContext';
import { db } from '../firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { growthPatternQuestions } from '../data/growth-pattern-diagnosis.js';

const GrowthPatternDiagnosis = () => {
  const [answers, setAnswers] = useState({});
  const [isFinished, setIsFinished] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const { user } = useAuth();
  const navigate = useNavigate();

  const totalQuestions = growthPatternQuestions.length;
  const answeredQuestions = Object.keys(answers).length;
  const progress = (answeredQuestions / totalQuestions) * 100;
  const isAllAnswered = answeredQuestions === totalQuestions;

  // 認証チェック
  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
    setError(''); // エラーメッセージをクリア
  };

  const calculateAndSaveResults = async () => {
    if (!isAllAnswered) {
      setError('すべての質問に回答してください。');
      return;
    }

    if (!user) {
      setError('ログインが必要です。');
      return;
    }
    
    setIsSaving(true);
    setError('');

    try {
      const factors = {
        initialConfidence: { total: 0, count: 0 },
        selfAwareness: { total: 0, count: 0 },
        resilience: { total: 0, count: 0 },
        planning: { total: 0, count: 0 },
      };

      // 各質問の回答を因子別に集計
      growthPatternQuestions.forEach(q => {
        const answerValue = answers[q.id];
        if (answerValue !== undefined && factors[q.factor]) {
          factors[q.factor].total += answerValue;
          factors[q.factor].count += 1;
        }
      });

      // 因子別の平均スコアを計算
      const growthPatternProfile = {
        initialConfidence: factors.initialConfidence.count > 0 
          ? (factors.initialConfidence.total / factors.initialConfidence.count) : 0,
        selfAwareness: factors.selfAwareness.count > 0 
          ? (factors.selfAwareness.total / factors.selfAwareness.count) : 0,
        resilience: factors.resilience.count > 0 
          ? (factors.resilience.total / factors.resilience.count) : 0,
        planning: factors.planning.count > 0 
          ? (factors.planning.total / factors.planning.count) : 0,
      };

      // 各回答の詳細も保存
      const detailedAnswers = {};
      growthPatternQuestions.forEach(q => {
        detailedAnswers[q.id] = {
          value: answers[q.id],
          factor: q.factor,
          question: q.text
        };
      });

      // Firestoreに保存
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, {
        growthPatternProfile: growthPatternProfile,
        growthPatternAnswers: detailedAnswers,
        lastGrowthPatternDiagnosis: new Date().toISOString(),
      }, { merge: true });
      
      console.log('成長パターン診断結果を保存しました:', growthPatternProfile);
      setIsFinished(true);

    } catch (error) {
      console.error("診断結果の保存に失敗:", error);
      setError("エラーが発生しました。もう一度お試しください。");
    } finally {
      setIsSaving(false);
    }
  };

  const handleBackToDashboard = () => {
    navigate('/dashboard?tab=patterns');
  };

  const handleRestart = () => {
    setAnswers({});
    setIsFinished(false);
    setError('');
  };

  // 認証チェック
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-600">ログインが必要です...</p>
        </div>
      </div>
    );
  }

  // 完了画面
  if (isFinished) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 p-4">
        <div className="max-w-2xl mx-auto p-8 bg-white rounded-lg shadow-lg text-center my-12">
          <Award className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-900 mb-4">診断完了！</h1>
          <p className="text-gray-600 mb-8">
            ありがとうございます。あなたの成長パターンが分析されました。
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={handleBackToDashboard}
              className="px-8 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              ダッシュボードで結果を見る
            </button>
            <button 
              onClick={handleRestart}
              className="px-8 py-3 bg-gray-500 text-white rounded-lg font-semibold hover:bg-gray-600 transition-colors"
            >
              もう一度診断する
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 診断画面
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-3xl mx-auto p-4 sm:p-8">
        <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8">
          <div className="text-center mb-8">
            <RefreshCcw className="w-12 h-12 text-blue-600 mx-auto mb-2" />
            <h1 className="text-2xl font-bold text-gray-900">成長パターン診断</h1>
            <p className="text-gray-500 mt-1">
              あなたの学習スタイルや成長の癖を特定します。
            </p>
          </div>

          <div className="mb-8">
            <div className="flex justify-between text-sm text-gray-600 mb-1">
              <span>進捗 ({answeredQuestions}/{totalQuestions})</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div 
                className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" 
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <div className="space-y-6">
            {growthPatternQuestions.map((q, index) => (
              <div key={q.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <p className="font-medium text-gray-800 mb-4">
                  {index + 1}. {q.text}
                </p>
                <div className="flex flex-col sm:flex-row justify-center space-y-2 sm:space-y-0 sm:space-x-2 flex-wrap gap-2">
                  {[
                    '全くそう思わない',
                    'あまりそう思わない', 
                    'どちらでもない', 
                    'ややそう思う', 
                    '非常にそう思う'
                  ].map((label, value) => {
                    const score = value + 1;
                    const isSelected = answers[q.id] === score;
                    return (
                      <button 
                        key={score} 
                        onClick={() => handleAnswer(q.id, score)}
                        className={`px-3 py-2 text-xs sm:text-sm rounded-md transition-colors border-2 ${
                          isSelected
                            ? 'bg-blue-600 text-white border-blue-600 shadow-md' 
                            : 'bg-white hover:bg-blue-50 border-gray-300 text-gray-700'
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <button 
              onClick={calculateAndSaveResults} 
              disabled={!isAllAnswered || isSaving} 
              className={`w-full sm:w-auto px-10 py-4 rounded-lg font-bold text-lg flex items-center justify-center gap-2 mx-auto transition-colors ${
                isAllAnswered && !isSaving
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-400 text-gray-200 cursor-not-allowed'
              }`}
            >
              {isSaving ? (
                <>
                  <RefreshCcw className="w-6 h-6 animate-spin" />
                  分析中...
                </>
              ) : (
                <>
                  <CheckCircle className="w-6 h-6" />
                  結果を見る
                </>
              )}
            </button>
            
            {!isAllAnswered && (
              <p className="text-sm text-gray-500 mt-2">
                すべての質問に回答してください ({answeredQuestions}/{totalQuestions})
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GrowthPatternDiagnosis;